import React, { useRef, useMemo } from 'react'
import PropTypes from 'prop-types'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import styles from './RichTextContentEditor.module.css'

const isQuillEmpty = (html) => {
  if (!html || typeof html !== 'string') {
    return true
  }
  return html.replace(/<[^>]+>/g, '').trim() === ''
}

const stripFontColorFromDelta = delta => {
  if (!delta || !Array.isArray(delta.ops)) {
    return delta
  }

  // Keep the original Delta instance so Quill methods (e.g. insert) remain available.
  delta.ops = delta.ops.map(op => {
    if (!op?.attributes?.color) {
      return op
    }

    const nextOp = { ...op, attributes: { ...op.attributes } }
    delete nextOp.attributes.color
    if (Object.keys(nextOp.attributes).length === 0) {
      delete nextOp.attributes
    }
    return nextOp
  })

  return delta
}

const RichTextContentEditor = ({ onChange, value }) => {
  const quillRef = useRef()
  const normalizedValue = typeof value === 'string' ? value : ''
  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          ['bold', 'italic', 'underline'],
          ['link'],
          [{ align: ['', 'center', 'right', 'justify'] }],
          [{ list: 'ordered' }, { list: 'bullet' }]
        ]
      },
      clipboard: {
        matchVisual: false,
        // Preserve rich formatting on paste but force default app text color.
        matchers: typeof Node !== 'undefined'
          ? [[Node.ELEMENT_NODE, (_node, delta) => stripFontColorFromDelta(delta)]]
          : []
      }
    }),
    []
  )

  const formats = [
    'bold',
    'italic',
    'underline',
    'align',
    'list',
    'link',
    'bullet',
    'indent',
    'clean'
  ]

  return (
    <div className={styles.wrapper}>
      <ReactQuill
        ref={quillRef}
        className={styles.editor}
        formats={formats}
        modules={modules}
        onChange={(content, _delta, source) => {
          if (source !== 'user') {
            // Quill emits empty api updates when editors unmount (e.g. Collapse panels).
            if (isQuillEmpty(content) && normalizedValue && !isQuillEmpty(normalizedValue)) {
              return
            }
            return
          }
          if (typeof onChange === 'function') {
            onChange(content)
          }
        }}
        theme='snow'
        value={normalizedValue}
      />
    </div>
  )
}

RichTextContentEditor.propTypes = {
  onChange: PropTypes.func,
  value: PropTypes.string
}

RichTextContentEditor.defaultProps = {
  onChange: undefined,
  value: ''
}

export default RichTextContentEditor
